import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../design/button";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../../firebase";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Heading } from "../../../design/heading";
import { Text } from "../../../design/text";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Checkbox, CheckboxField } from "../../../design/checkbox";
import { Label } from "../../../design/fieldset";
import { Link } from "../../../design/link";
import { Card } from "../../../design/card";
import { doc, updateDoc } from "firebase/firestore";
import { useAccount } from "../../../contexts/AccountContext";
import { CheckIcon, CreditCardIcon, UserGroupIcon, UserIcon } from "@heroicons/react/20/solid";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY || "");

const StripeContainer: React.FC = () => {
  const navigate = useNavigate();
  const [clientSecret] = useState<string | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleStartProcess = async () => {
    setIsLoading(true);
    try {
      const result = await httpsCallable(
        functions,
        "generateCheckoutSession",
      )({});
      const data = result.data as { url: string };
      window.location.replace(data.url);
    } catch (error) {
      console.error("Error: ", JSON.stringify(error));
      alert(
        "Failed to generate Stripe Checkout Session. Please try again later",
      );
    }
    setIsLoading(false);
  };

  const handleStepComplete = () => {
    navigate("/dashboard");
  };

  if (!clientSecret) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="space-y-2 text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
              <CreditCardIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <Heading>Simple, Transparent Pricing</Heading>
          <Text className="text-lg text-gray-600 dark:text-gray-400">
            Pay only for what you use with no hidden fees
          </Text>
        </div>

        {/* Free Trial Banner */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 p-4 text-center text-white shadow-lg">
          <div className="absolute inset-0 bg-white/10 backdrop-blur-sm" />
          <div className="relative">
            <span className="text-2xl font-bold">30-Day Free Trial</span>
            <p className="mt-1 text-green-100">Start using VisitProof today - no charge until your trial ends</p>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Admin Seat Pricing */}
          <Card className="relative border-2 border-blue-200 dark:border-blue-800">
            <div className="absolute -top-3 left-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full dark:bg-blue-900/50 dark:text-blue-300">
                <UserIcon className="w-3.5 h-3.5" />
                Admin Seats
              </span>
            </div>
            <div className="pt-4 text-center">
              <div className="flex items-baseline justify-center gap-1 mb-1">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">$25</span>
                <span className="text-gray-500 dark:text-gray-400">/month</span>
              </div>
              <Text className="text-sm text-gray-600 dark:text-gray-400">per admin user</Text>
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Text className="text-xs text-gray-500 dark:text-gray-400">
                  Charged when you add team members with admin access
                </Text>
              </div>
            </div>
          </Card>

          {/* Supervisor Pricing */}
          <Card className="relative border-2 border-purple-200 dark:border-purple-800">
            <div className="absolute -top-3 left-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full dark:bg-purple-900/50 dark:text-purple-300">
                <UserGroupIcon className="w-3.5 h-3.5" />
                Supervisors
              </span>
            </div>
            <div className="pt-4 text-center">
              <div className="flex items-baseline justify-center gap-1 mb-1">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">$25</span>
                <span className="text-gray-500 dark:text-gray-400">/month</span>
              </div>
              <Text className="text-sm text-gray-600 dark:text-gray-400">per active supervisor</Text>
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Text className="text-xs text-gray-500 dark:text-gray-400">
                  Only charged for supervisors who conduct visits
                </Text>
              </div>
            </div>
          </Card>
        </div>

        {/* What's Included */}
        <Card>
          <Text className="font-semibold text-gray-900 dark:text-white mb-4">
            Everything included with your subscription:
          </Text>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "Unlimited visits & cases",
              "Professional report generation",
              "Real-time GPS tracking",
              "Photo & video documentation",
              "Invoice & payment processing",
              "Calendar integration",
              "Team messaging",
              "Priority support",
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-2">
                <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                <Text className="text-sm text-gray-700 dark:text-gray-300">{feature}</Text>
              </div>
            ))}
          </div>
        </Card>

        {/* Terms Agreement */}
        <Card className="border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
          <CheckboxField className="items-start">
            <Checkbox
              name="termsAgreement"
              value="agree"
              checked={agreedToTerms}
              onChange={() => setAgreedToTerms(!agreedToTerms)}
              className="mt-1"
            />
            <Label className="text-sm leading-relaxed">
              I agree to the{" "}
              <Link href="https://visitproof.com/terms" target="_blank" className="font-semibold underline">
                terms and conditions
              </Link>{" "}
              and understand that I will be charged based on the number of active admins and supervisors after my free trial ends.
            </Label>
          </CheckboxField>
        </Card>

        {/* Action Button */}
        <div className="space-y-3">
          <Button
            color="blue"
            disabled={!agreedToTerms || isLoading}
            className="w-full py-3 text-base font-semibold"
            onClick={handleStartProcess}
          >
            {isLoading ? "Processing..." : "Start Your Free Trial"}
          </Button>
        </div>

        {/* Additional Information */}
        <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
          <div className="p-4 space-y-2 rounded-lg bg-gray-50 dark:bg-zinc-800/50">
            <Text className="font-medium text-gray-900 dark:text-gray-200">
              How billing works:
            </Text>
            <ul className="space-y-1 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">•</span>
                <span>Your 30-day free trial starts today - no credit card charged</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">•</span>
                <span>Admin seats are billed when you invite team members</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">•</span>
                <span>Supervisor fees are based on monthly activity</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">•</span>
                <span>Cancel anytime with no long-term commitment</span>
              </li>
            </ul>
          </div>

          <div className="text-center">
            <Text>
              Questions? Contact us at{" "}
              <Link href="mailto:info@visitproof.com" className="font-semibold">
                info@visitproof.com
              </Link>
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret: clientSecret! }}>
      <StripeStep clientSecret={clientSecret} onStepComplete={handleStepComplete} />
    </Elements>
  );
};

interface StripeStepProps {
  clientSecret: string;
  onStepComplete: () => void;
}

const StripeStep: React.FC<StripeStepProps> = ({
  onStepComplete,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { account } = useAccount();
  const [isLoading, setIsLoading] = useState(false);

  const handlePaymentSubmission = async () => {
    setIsLoading(true);
    if (!stripe || !elements) return;

    try {
      const paymentResult = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });

      if (paymentResult.error) {
        console.error("Payment error:", paymentResult.error.message);
        alert("Payment failed. Please check your details and try again.");
      } else {
        await updateDoc(doc(db, "accounts", account.id), {
          isOnboardingComplete: true,
        });
        onStepComplete();
      }
    } catch (error) {
      console.error("Error in payment processing:", error);
      alert("An error occurred during payment. Please try again.");
    }
    setIsLoading(false);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="space-y-2 text-center">
        <Heading>Complete Your Setup</Heading>
        <Text className="text-lg text-gray-600 dark:text-gray-400">
          Enter your payment details to activate your account
        </Text>
      </div>

      {/* Pricing Summary */}
      <Card className="bg-gray-50 dark:bg-zinc-800/50">
        <div className="flex items-center justify-between">
          <div>
            <Text className="font-semibold text-gray-900 dark:text-white">
              Usage-Based Pricing
            </Text>
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              $25/month per admin + $25/month per active supervisor
            </Text>
          </div>
          <span className="inline-flex items-center px-3 py-1 text-sm font-semibold text-green-800 bg-green-100 rounded-full dark:bg-green-900/30 dark:text-green-400">
            30-Day Free Trial
          </span>
        </div>
      </Card>

      {/* Payment Form Card */}
      <Card>
        <div className="py-2">
          <PaymentElement />
        </div>
      </Card>

      {/* Security Notice */}
      <div className="p-4 border border-blue-200 rounded-lg bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 text-xl">🔒</span>
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <Text className="mb-1 font-semibold text-gray-900 dark:text-white">
              Secure Payment
            </Text>
            <Text>
              Your payment information is encrypted and securely processed by Stripe.
              We never store your full card details.
            </Text>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <Button
        color="blue"
        className="w-full py-3 text-base font-semibold"
        onClick={handlePaymentSubmission}
        disabled={isLoading}
      >
        {isLoading ? "Processing..." : "Start Free Trial"}
      </Button>

      {/* Help Text */}
      <Text className="text-sm text-center text-gray-600 dark:text-gray-400">
        Your card will not be charged until after your 30-day free trial ends.
      </Text>
    </div>
  );
};

export default StripeContainer;
